import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, LimitExceededError } from '../lib/api'
import { supabase } from '../lib/supabase'

interface UploadResponse {
  job_id: string
  status: string
  message_it: string
}

export interface JobListItem {
  job_id: string
  status: string
  file_name: string | null
  rows_total: number | null
  rows_valid: number | null
  rows_error: number | null
  created_at: string | null
  completed_at: string | null
  message_it: string
}

export interface JobStatus {
  job_id: string
  status: string
  progress_pct: number
  rows_total?: number
  rows_valid?: number
  rows_error?: number
  errors_preview: Array<Record<string, unknown>>
  message_it: string
  data_source_id?: string | null
}

export function useUploadFile() {
  return useMutation<UploadResponse, Error, { file: File; defaultBuId?: string | null; industry?: string | null }>({
    mutationFn: async ({ file, defaultBuId, industry }) => {
      const form = new FormData()
      form.append('file', file)
      if (defaultBuId) form.append('default_bu_id', defaultBuId)
      if (industry) form.append('industry', industry)
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      const apiBase = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
      const res = await fetch(`${apiBase}/api/v1/upload`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        if (res.status === 402 && body?.detail?.error === 'limit_exceeded') {
          throw new LimitExceededError(body.detail)
        }
        throw new Error(body?.detail ?? `Upload fallito: ${res.status}`)
      }
      return res.json()
    },
  })
}

export function useJobStatus(jobId: string | null) {
  return useQuery<JobStatus>({
    queryKey: ['job', jobId],
    queryFn: () => api.get<JobStatus>(`/api/v1/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'completed' || status === 'failed' ? false : 2000
    },
  })
}

/**
 * WebSocket hook for real-time ingestion progress.
 * Connects to ws://{api}/ws/ingestion/{jobId}.
 * Returns null until first message arrives.
 */
export function useJobStatusWS(jobId: string | null): JobStatus | null {
  const [data, setData] = useState<JobStatus | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    if (!jobId) {
      setData(null)
      return
    }

    const apiBase = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
    const wsUrl = `${apiBase.replace(/^https?/, (p: string) => (p === 'https' ? 'wss' : 'ws'))}/ws/ingestion/${jobId}`

    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrl)
    } catch {
      return
    }

    wsRef.current = ws

    ws.onmessage = (e) => {
      try {
        const parsed = JSON.parse(e.data) as JobStatus
        setData(parsed)
        // Close WS once terminal state reached
        if (parsed.status === 'completed' || parsed.status === 'failed') {
          ws.close()
        }
      } catch {
        // ignore malformed messages
      }
    }

    ws.onerror = () => ws.close()

    return () => {
      ws.close()
      wsRef.current = null
    }
  }, [jobId])

  // Reset data when jobId changes
  useEffect(() => {
    if (!jobId) setData(null)
  }, [jobId])

  return data
}

export function useJobHistory(limit = 50) {
  return useQuery<JobListItem[]>({
    queryKey: ['jobs'],
    queryFn: () => api.get<JobListItem[]>(`/api/v1/jobs?limit=${limit}`),
    refetchOnWindowFocus: false,
  })
}

export function useDeleteJob() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (jobId: string) => api.delete(`/api/v1/jobs/${jobId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}
